import { hash3i, toUnit } from '@/core/math/hash';
import { clamp01 } from '@/core/math/scalar';
import { getBiome } from './BiomeDef';
import {
  BIOME_VEGETATION,
  UNDERWATER_DENSITY,
  UNDERWATER_VEGETATION,
  VegetationSpeciesDef,
  isGroundCoverType,
} from './VegetationDef';
import { TerrainParams } from './TerrainPipeline';

/**
 * Where plants go, as a pure function of (cell, seed) — nothing here is stored
 * and nothing is persisted, exactly like `DepositField.depositAt`.
 *
 * The one property everything below exists to guarantee is **placement is
 * independent of the LOD level that asks**. Vegetation is generated on the
 * four finest chunk levels (see `PlanetSurface.VEGETATION_LOD_DEPTH`), so the
 * same patch of ground is scattered by a level-16 chunk when the camera is
 * close and by its level-13 ancestor when it is not. Those two answers have to
 * agree, or approaching a forest visibly re-rolls it.
 *
 * They used to disagree completely. The scatter cell was derived from the
 * *chunk's own vertex spacing* (`cellArea = sampleSpacing^2`), which quadruples
 * with every level out, so the two levels quantised the same ground into
 * different integer cells and hashed unrelated numbers; instances were then
 * placed on the mesh vertex that happened to roll them, which is a different
 * grid again. Measured on Halcyon: of 289 level-14 trees standing on ground a
 * level-16 chunk also covered, exactly **1** landed within half a metre of a
 * level-16 tree, and the mean nearest-neighbour distance was 100 m. Every
 * approach re-grew the forest somewhere else, and every level re-did the work.
 *
 * The fix is a single scatter grid that belongs to the *planet*, not to the
 * chunk:
 *
 *  - Cells are a fixed power-of-two subdivision of a cube face
 *    (`vegetationCellsPerFaceEdge`), so a chunk boundary at any level always
 *    falls exactly on a cell boundary and every cell belongs to exactly one
 *    chunk per level — no seams, no double-emitted plants.
 *  - A cell's identity, and therefore every roll made about it, is
 *    `(face, cellU, cellV)`. No level appears in it anywhere.
 *  - A coarse chunk emits a deterministic *subset* of the same cells — every
 *    `vegetationCellStride(lodDelta)`-th one in each axis. The subsets are
 *    nested (multiples of four are multiples of two), so a tree seen from far
 *    away is the same tree, in the same place, when you walk up to it; the
 *    rest fade in around it rather than replacing it.
 */

export type VegetationPopulation = 'structure' | 'groundCover';

/**
 * Salts separating the rolls made about one cell. Independent hashes, never
 * XORed variations of one: presence accepts only a narrow interval of its
 * hash, and XOR preserves enough of that structure to correlate whatever is
 * derived from it (this cost a session once — every cell picked the first
 * species in its biome's list).
 */
const PRESENCE_SALT: Record<VegetationPopulation, number> = {
  structure: 771117,
  groundCover: 771119,
};

/**
 * Cells per finest-level chunk edge, as a power-of-two exponent.
 *
 * This is what actually bounds the scatter loop. Combined with the stride
 * below, *every* vegetated chunk at every level iterates exactly
 * `(1 << this)^2` cells — 4096 — regardless of the body's radius, the quality
 * tier's cell size, or how many levels deep the LOD tree goes. Without it a
 * configuration whose ideal cell size is much finer than its finest chunk
 * (a coarse `targetVertexSpacing` on a large body, which is what the test
 * profile is) would ask one chunk to walk tens of millions of cells.
 */
const MAX_CELL_LEVELS_BELOW_FINEST_CHUNK = 6;

/**
 * Cells along a full cube-face edge, always a power of two.
 *
 * Power-of-two is not a rounding convenience, it is the whole reason chunk
 * edges never cut a cell in half: a chunk at level L spans `2 / 2^L` in
 * face-uv, a cell spans `2 / cellsPerFaceEdge`, and while the latter divides
 * the former every chunk contains a whole number of whole cells. Round the
 * cell count to anything else and cells straddle chunk borders, which shows up
 * as a line of missing or doubled plants along every LOD seam.
 *
 * The returned count is therefore a target, not an exact metre size: the real
 * cell lands within a factor of sqrt(2) of `cellMetres`, and coarser still if
 * `MAX_CELL_LEVELS_BELOW_FINEST_CHUNK` binds.
 */
export function vegetationCellsPerFaceEdge(radius: number, cellMetres: number, maxLevel: number): number {
  // A face's u spans -1..1, which is a quarter of the way around the body.
  const faceArc = (Math.PI / 2) * radius;
  const ideal = Math.round(Math.log2(Math.max(1, faceArc / Math.max(0.25, cellMetres))));
  const ceiling = Math.max(1, Math.round(maxLevel)) + MAX_CELL_LEVELS_BELOW_FINEST_CHUNK;
  return 1 << Math.min(26, Math.max(1, Math.min(ideal, ceiling)));
}

/** Ground area one cell of `cellsPerFaceEdge` represents, m². */
export function vegetationCellArea(radius: number, cellsPerFaceEdge: number): number {
  const side = ((Math.PI / 2) * radius) / cellsPerFaceEdge;
  return side * side;
}

/**
 * Spacing, in cells, between the cells a chunk `lodDelta` levels coarser than
 * the finest actually visits.
 *
 * A power of two, and exactly the factor by which that chunk is wider, so the
 * cell count it walks — and therefore its instance payload and its share of
 * worker time — is the same at every level. That is the whole reason this is a
 * stride and not a probability: thinning by a hash roll would have the
 * coarsest chunks hashing 64 cells for every one they keep, which is 64x the
 * work to draw the same distant forest.
 *
 * Nesting is free here. The cells a stride-4 chunk visits are a subset of the
 * stride-2 ones, which are a subset of all of them, because a chunk's first
 * cell index is always a multiple of its own cell count and therefore of the
 * stride. A plant is never re-rolled by a finer level, only joined by
 * neighbours.
 */
export function vegetationCellStride(lodDelta: number): number {
  return 1 << Math.min(8, Math.max(0, Math.round(lodDelta)));
}

/**
 * What grows in one cell, or null for bare ground.
 *
 * Deliberately carries no position: the caller owns the cell's geometry (it
 * has the direction and the interpolated terrain radius already) and knowing
 * it here would only invite re-deriving it a second, subtly different way.
 * `scale` is a normalised 0..1 draw against the species' `heightRange`/
 * `radiusRange`, not metres — the renderer decides the actual size from the
 * species definition, keeping this struct independent of any geometry choice.
 */
export interface VegetationSample {
  speciesId: string;
  scale: number;
  rotationY: number;
  colorJitter: number;
}

/**
 * Rolls one population for one cell.
 *
 * `cellArea` is the planet's fixed cell area (`vegetationCellArea`), never the
 * calling chunk's vertex spacing — see this module's doc comment for what
 * happened when it was the latter.
 *
 * `densityScale` multiplies the biome's authored plants-per-km². It is the
 * quality tier's count knob, and it is safe to turn precisely because the
 * presence roll is a fixed per-cell number compared against a threshold:
 * lowering the scale only ever *removes* plants, it never relocates the ones
 * that remain. Cell size cannot do this job — halving it quarters each cell's
 * probability while quadrupling the cell count, so the plant count is
 * unchanged and only the spacing granularity improves.
 */
export function vegetationSampleAt(
  cellU: number,
  cellV: number,
  face: number,
  biomeId: string,
  underwater: boolean,
  depth: number,
  slope: number,
  cellArea: number,
  densityScale: number,
  params: TerrainParams,
  population: VegetationPopulation,
): VegetationSample | null {
  let candidates: VegetationSpeciesDef[];
  let densityPerKm2: number;

  if (underwater) {
    if (biomeId !== 'ocean') return null;
    candidates = UNDERWATER_VEGETATION[biomeId] ?? [];
    densityPerKm2 = UNDERWATER_DENSITY[biomeId] ?? 0;
    candidates = candidates.filter((s) => s.maxDepth !== undefined && depth <= s.maxDepth);
  } else {
    candidates = BIOME_VEGETATION[biomeId] ?? [];
    const biome = getBiome(biomeId);
    // scatterDensity is the same per-km2 density knob as vegetationDensity,
    // authored for every biome (including the barren ones) but never
    // consumed until rock/crystal species existed to use it. A biome with
    // live vegetation always uses vegetationDensity, unchanged from before;
    // scatterDensity only takes over where that is zero, which today means
    // exactly the biomes whose only BIOME_VEGETATION entries are rock/crystal.
    densityPerKm2 = biome.vegetationDensity > 0 ? biome.vegetationDensity : biome.scatterDensity;
    candidates = candidates.filter((s) => slope <= s.maxSlope);
  }

  const wantGroundCover = population === 'groundCover';
  candidates = candidates.filter((s) => isGroundCoverType(s.type) === wantGroundCover);

  if (candidates.length === 0 || densityPerKm2 <= 0) return null;

  const salt = PRESENCE_SALT[population];
  const presence = toUnit(hash3i(cellU, cellV, face, params.seed + salt));
  const probability = clamp01((densityPerKm2 / 1_000_000) * cellArea * Math.max(0, densityScale));
  if (presence > probability) return null;

  const pickHash = hash3i(cellU, cellV, face, params.seed + salt + 1013);
  let totalWeight = 0;
  for (const c of candidates) totalWeight += c.weight;
  let roll = toUnit(pickHash) * totalWeight;
  let chosen = candidates[candidates.length - 1];
  for (const c of candidates) {
    roll -= c.weight;
    if (roll <= 0) {
      chosen = c;
      break;
    }
  }

  return {
    speciesId: chosen.id,
    scale: toUnit(hash3i(cellU, cellV, face, params.seed + salt + 2029)),
    rotationY: toUnit(hash3i(cellU, cellV, face, params.seed + salt + 3037)) * Math.PI * 2,
    colorJitter: toUnit(hash3i(cellU, cellV, face, params.seed + salt + 4051)),
  };
}
